import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["list", "listContainer", "searchInput", "dropdown", "selectedContent",
    "selectedImage", "selectedPlaceholder", "selectedName", "addBtn", "publishBtn", "publishedBadge"]
  static values = { createUrl: String, searchUrl: String, isOwner: Boolean, reorderUrl: String, publishUrl: String, unpublishUrl: String, isPublished: Boolean }

  connect() {
    this.debounceTimer = null
    this.selectedContentId = null
    this.removeTopContentId = null
    this.sortable = null
    this.addModal = null
    this.removeModal = null
    this.boundHideDropdown = this.hideDropdown.bind(this)
    document.addEventListener("click", this.boundHideDropdown)

    const addModalEl = document.getElementById("topContentAddModal")
    if (addModalEl) {
      this.addModal = new bootstrap.Modal(addModalEl)
      addModalEl.addEventListener("shown.bs.modal", () => {
        if (this.hasSearchInputTarget) this.searchInputTarget.focus()
      })
    }

    const removeModalEl = document.getElementById("topContentRemoveModal")
    if (removeModalEl) this.removeModal = new bootstrap.Modal(removeModalEl)

    const publishModalEl = document.getElementById("topContentPublishModal")
    if (publishModalEl) this.publishModal = new bootstrap.Modal(publishModalEl)

    const unpublishModalEl = document.getElementById("topContentUnpublishModal")
    if (unpublishModalEl) this.unpublishModal = new bootstrap.Modal(unpublishModalEl)

    this.initSortable()
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
    document.removeEventListener("click", this.boundHideDropdown)
    if (this.sortable) this.sortable.destroy()
  }

  initSortable() {
    if (!this.isOwnerValue || !this.hasListTarget) return
    if (this.sortable) this.sortable.destroy()
    this.sortable = new Sortable(this.listTarget, {
      handle: ".top-content-drag-handle",
      animation: 150,
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      onEnd: () => {
        this.reorderRanks()
        this.persistOrder()
      }
    })
  }

  persistOrder() {
    const ids = Array.from(this.listTarget.querySelectorAll(".top-content-item"))
      .map(item => item.dataset.topContentItemId)
    const csrfToken = document.querySelector("[name='csrf-token']").content

    fetch(this.reorderUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ ids })
    })
  }

  showAddModal() {
    this.resetAddModal()
    if (this.addModal) this.addModal.show()
  }

  showPublishModal() {
    if (this.publishModal) this.publishModal.show()
  }

  publishTop10() {
    const csrfToken = document.querySelector("[name='csrf-token']").content

    fetch(this.publishUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken }
    })
      .then(r => r.json())
      .then(data => {
        if (!data.success) return
        window.location.reload()
      })
  }

  showUnpublishModal() {
    if (this.unpublishModal) this.unpublishModal.show()
  }

  unpublishTop10() {
    const csrfToken = document.querySelector("[name='csrf-token']").content

    fetch(this.unpublishUrlValue, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken }
    })
      .then(r => r.json())
      .then(data => {
        if (!data.success) return
        window.location.reload()
      })
  }

  showRemoveModal(event) {
    const btn = event.currentTarget
    this.removeTopContentId = btn.dataset.topContentId
    const nameEl = document.getElementById("topContentRemoveName")
    if (nameEl) nameEl.textContent = btn.dataset.topContentName
    if (this.removeModal) this.removeModal.show()
  }

  search() {
    clearTimeout(this.debounceTimer)
    const query = this.searchInputTarget.value.trim()

    if (query.length < 2) {
      this.clearDropdown()
      return
    }

    this.debounceTimer = setTimeout(() => this.fetchResults(query), 300)
  }

  fetchResults(query) {
    const url = `${this.searchUrlValue}?q=${encodeURIComponent(query)}`
    fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(r => r.json())
      .then(results => {
        if (!Array.isArray(results)) return
        this.renderDropdown(results)
      })
  }

  renderDropdown(results) {
    if (results.length === 0) {
      this.dropdownTarget.innerHTML = '<div class="top-content-dropdown-empty">No results found</div>'
      this.dropdownTarget.classList.remove("d-none")
      return
    }

    this.dropdownTarget.innerHTML = results.map(c =>
      `<div class="top-content-dropdown-item"
            data-action="click->top-content#selectContent"
            data-content-id="${c.id}"
            data-content-name="${c.name}"
            data-content-image-url="${c.image_url || ''}">${c.name}</div>`
    ).join("")
    this.dropdownTarget.classList.remove("d-none")
  }

  selectContent(event) {
    const item = event.currentTarget
    this.selectedContentId = item.dataset.contentId
    this.searchInputTarget.value = item.dataset.contentName
    this.selectedNameTarget.textContent = item.dataset.contentName

    const imageUrl = item.dataset.contentImageUrl
    if (imageUrl) {
      this.selectedImageTarget.src = imageUrl
      this.selectedImageTarget.classList.remove("d-none")
      this.selectedPlaceholderTarget.classList.add("d-none")
    } else {
      this.selectedImageTarget.classList.add("d-none")
      this.selectedPlaceholderTarget.classList.remove("d-none")
    }

    this.selectedContentTarget.classList.remove("d-none")
    this.addBtnTarget.disabled = false
    this.clearDropdown()
  }

  addContent() {
    if (!this.selectedContentId) return

    const csrfToken = document.querySelector("[name='csrf-token']").content
    this.addBtnTarget.disabled = true

    fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ content_id: this.selectedContentId })
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) {
          const errorEl = document.getElementById("topContentAddError")
          if (errorEl) {
            errorEl.textContent = data.error
            errorEl.classList.remove("d-none")
          }
          this.addBtnTarget.disabled = false
          return
        }

        this.appendItem(data)
        if (this.addModal) this.addModal.hide()
        this.updateAddButton()
      })
  }

  removeContent() {
    if (!this.removeTopContentId) return

    const csrfToken = document.querySelector("[name='csrf-token']").content
    const url = `${this.createUrlValue}/${this.removeTopContentId}`

    fetch(url, {
      method: "DELETE",
      headers: { "X-CSRF-Token": csrfToken }
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          const item = this.element.querySelector(`[data-top-content-item-id="${this.removeTopContentId}"]`)
          if (item) item.remove()

          this.reorderRanks()
          if (this.removeModal) this.removeModal.hide()
          this.updateAddButton()
          this.updateEmptyState()
        }
      })
  }

  appendItem(data) {
    const imageHtml = data.content.image_url
      ? `<img src="${data.content.image_url}" alt="${data.content.name}" class="top-content-thumb">`
      : `<div class="top-content-thumb-placeholder"><i class="bi bi-film"></i></div>`

    const dragHandle = this.isOwnerValue
      ? `<span class="top-content-drag-handle"><i class="bi bi-grip-vertical"></i></span>`
      : ""

    const removeBtn = `<button class="btn btn-danger btn-sm"
              data-action="click->top-content#showRemoveModal"
              data-top-content-id="${data.id}"
              data-top-content-name="${data.content.name}">
              <i class="bi bi-trash"></i>
            </button>`

    const li = document.createElement("li")
    li.className = "top-content-item"
    li.dataset.topContentItemId = data.id
    li.innerHTML = `
      ${dragHandle}
      <span class="top-content-rank">${data.position}</span>
      ${imageHtml}
      <span class="top-content-name flex-grow-1">
        <a href="/content/${data.content.slug}">${data.content.name}</a>
      </span>
      ${removeBtn}
    `

    if (this.hasListTarget) {
      this.listTarget.appendChild(li)
    } else {
      const ol = document.createElement("ol")
      ol.className = "top-content-list"
      ol.setAttribute("data-top-content-target", "list")
      this.listContainerTarget.appendChild(ol)
      ol.appendChild(li)
      this.initSortable()
    }

    this.hideEmptyState()
  }

  reorderRanks() {
    if (!this.hasListTarget) return
    const items = this.listTarget.querySelectorAll(".top-content-item")
    items.forEach((item, idx) => {
      item.querySelector(".top-content-rank").textContent = idx + 1
    })
  }

  updateAddButton() {
    const count = this.hasListTarget ? this.listTarget.querySelectorAll(".top-content-item").length : 0
    const addBtns = this.element.querySelectorAll("[data-action*='showAddModal']")
    addBtns.forEach(btn => {
      btn.classList.toggle("d-none", count >= 10)
    })
    const publishBtns = this.element.querySelectorAll("[data-action*='showPublishModal']")
    publishBtns.forEach(btn => {
      btn.classList.toggle("d-none", count < 10)
    })
  }

  updateEmptyState() {
    const count = this.hasListTarget ? this.listTarget.querySelectorAll(".top-content-item").length : 0
    if (count === 0) this.showEmptyState()
  }

  hideEmptyState() {
    const empty = this.element.querySelector(".top-content-empty")
    if (empty) empty.classList.add("d-none")
  }

  showEmptyState() {
    const empty = this.element.querySelector(".top-content-empty")
    if (empty) empty.classList.remove("d-none")
  }

  resetAddModal() {
    this.selectedContentId = null
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    if (this.hasSelectedContentTarget) this.selectedContentTarget.classList.add("d-none")
    if (this.hasAddBtnTarget) this.addBtnTarget.disabled = true
    const errorEl = document.getElementById("topContentAddError")
    if (errorEl) errorEl.classList.add("d-none")
    this.clearDropdown()
  }

  clearDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.innerHTML = ""
      this.dropdownTarget.classList.add("d-none")
    }
  }

  hideDropdown(event) {
    if (this.hasDropdownTarget && !this.element.contains(event.target)) {
      this.clearDropdown()
    }
  }
}
